import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from '@/components/common';
import { AuthModal } from '@/components/modals/AuthModal';
import { useAppContext } from '@/hooks/useAppContext';
import { ApiRequestError } from '@/services/invitation-api';
import { FaCheckCircle, FaExclamationTriangle, FaRedo, FaSignInAlt, FaSpinner } from 'react-icons/fa';

interface ConfirmEmailLandingProps {
  token: string;
  /** Leave this view: into the app when signed in, to sign-in otherwise. */
  onDone: () => void;
}

type Outcome =
  | { kind: 'done'; email: string }
  | { kind: 'dead' }
  | { kind: 'taken' }
  | { kind: 'failed'; message: string };

/**
 * The `/confirm-email#<token>` screen: the link a self-service email change
 * sent to the new address. It confirms only with the session of the account
 * that asked, so a signed-out visitor signs in here first; the token stays in
 * memory (App state and this component) meanwhile, never in storage or the URL.
 *
 * The token is single-use, so it is sent once per session — StrictMode's
 * second effect run must not spend it again — and again only after a sign-in
 * (a 401 left it unspent) or when the user asks to retry a failure.
 */
export const ConfirmEmailLanding: React.FC<ConfirmEmailLandingProps> = ({ token, onDone }) => {
  const { auth } = useAppContext();
  const { confirmEmailChange, isAuthenticated, isRestoringSession, logout } = auth;
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const sentFor = useRef<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Back to the sign-in prompt with the token kept: the next sign-in sends it.
  const askToSignIn = useCallback(() => {
    sentFor.current = null;
    setOutcome(null);
    setIsSigningIn(false);
    logout();
  }, [logout]);

  const confirm = useCallback(async () => {
    setOutcome(null);
    try {
      const { email } = await confirmEmailChange(token);
      setOutcome({ kind: 'done', email });
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        // The session expired or was revoked: the link is unspent.
        setSessionEnded(true);
        askToSignIn();
      } else if (err instanceof ApiRequestError && err.code === 'email_change_invalid') {
        setOutcome({ kind: 'dead' });
      } else if (err instanceof ApiRequestError && err.code === 'email_taken') {
        setOutcome({ kind: 'taken' });
      } else if (err instanceof ApiRequestError && err.code === 'rate_limited') {
        setOutcome({ kind: 'failed', message: 'Too many attempts. Wait a few minutes, then try again.' });
      } else {
        setOutcome({
          kind: 'failed',
          message: err instanceof Error ? err.message : 'Could not confirm your email address'
        });
      }
    }
  }, [confirmEmailChange, token, askToSignIn]);

  // Send once a session exists: on load when already signed in, or right
  // after signing in here. No cleanup that discards the answer: under
  // StrictMode the first run is the one that sends.
  useEffect(() => {
    if (!isAuthenticated || sentFor.current === token) return;
    sentFor.current = token;
    void confirm();
  }, [isAuthenticated, confirm, token]);

  // The outcome replaces the spinner; take focus there so it is read out and
  // the next Tab reaches its buttons.
  useEffect(() => {
    if (outcome) resultRef.current?.focus();
  }, [outcome]);

  // The normal sign-in form; once it signs in, the effect above confirms.
  if (isSigningIn && !isAuthenticated) {
    return <AuthModal />;
  }

  const backLabel = isAuthenticated ? 'Continue to Minka' : 'Back to sign in';
  const needsSignIn = !outcome && !isAuthenticated && !isRestoringSession;

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      size="md"
      showCloseButton={false}
      className="bg-gradient-to-b from-game-dark/95 via-game-dark/90 to-game-medium/30 backdrop-blur-md"
    >
      <ModalHeader showCloseButton={false}>
        <div className="text-center w-full">
          <div className="flex items-center justify-center mb-4">
            <img src="/assets/logo/minka-logo-color.png" alt="Minka" className="h-20 w-auto object-contain" />
          </div>
          <h2 className="text-xl font-bold text-white">Confirm your new email address</h2>
        </div>
      </ModalHeader>

      <ModalBody>
        {!outcome && !needsSignIn && (
          <div role="status" className="flex items-center justify-center gap-3 py-4 text-gray-200">
            <FaSpinner className="animate-spin text-game-golden" aria-hidden="true" />
            <span className="font-medium">Confirming your new email address…</span>
          </div>
        )}

        {needsSignIn && (
          <div className="space-y-6">
            <div role="status" className="p-4 bg-game-medium/20 border border-game-medium/50 rounded-lg">
              <p className="font-medium text-white">
                {sessionEnded
                  ? 'Your session has ended. Sign in again to confirm your new email address'
                  : 'Sign in to confirm your new email address'}
              </p>
              <p className="mt-2 text-sm text-gray-200">
                Use the account that asked for the change. Your email address stays as it is until you do.
              </p>
            </div>
            <Button type="button" variant="primary" onClick={() => setIsSigningIn(true)} className="w-full px-6 py-3">
              <FaSignInAlt aria-hidden="true" />
              <span>Sign in</span>
            </Button>
          </div>
        )}

        {outcome?.kind === 'done' && (
          <div ref={resultRef} tabIndex={-1} className="space-y-6 text-center focus:outline-none">
            <div role="status" className="flex items-center justify-center gap-3 text-green-300">
              <FaCheckCircle className="flex-shrink-0" aria-hidden="true" />
              <span className="font-medium">
                Your email address is now <span className="break-all text-white">{outcome.email}</span>
              </span>
            </div>
            <p className="text-sm text-gray-200">
              Sign in and password reset links use this address from now on.
            </p>
            <Button type="button" variant="primary" onClick={onDone} className="px-6 py-3">
              {backLabel}
            </Button>
          </div>
        )}

        {outcome?.kind === 'dead' && (
          <ConfirmProblem
            resultRef={resultRef}
            title="This link is no longer valid, or belongs to another account"
            detail="Sign in to the account that asked for the change, then try again from Account Settings. Links work for one hour and only once, and asking for another change replaces the old link."
            backLabel={backLabel}
            onDone={onDone}
            secondary={{ label: 'Sign in to another account', onClick: askToSignIn }}
          />
        )}

        {outcome?.kind === 'taken' && (
          <ConfirmProblem
            resultRef={resultRef}
            title="That email address now belongs to another account"
            detail="Your email address was not changed. Choose a different one in Account Settings."
            backLabel={backLabel}
            onDone={onDone}
          />
        )}

        {outcome?.kind === 'failed' && (
          <ConfirmProblem
            resultRef={resultRef}
            title={outcome.message}
            detail="Try again in a moment."
            backLabel={backLabel}
            onDone={onDone}
            onRetry={() => void confirm()}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

interface ConfirmProblemProps {
  resultRef: React.RefObject<HTMLDivElement | null>;
  title: string;
  detail: string;
  backLabel: string;
  onDone: () => void;
  onRetry?: () => void;
  /** A second way out, offered as a text link under the main button. */
  secondary?: { label: string; onClick: () => void };
}

/** A failed confirmation: what went wrong, and the way out. */
const ConfirmProblem: React.FC<ConfirmProblemProps> = ({
  resultRef,
  title,
  detail,
  backLabel,
  onDone,
  onRetry,
  secondary
}) => {
  const link = onRetry ? { label: backLabel, onClick: onDone } : secondary;

  return (
    <div ref={resultRef} tabIndex={-1} className="space-y-6 focus:outline-none">
      <div role="alert" className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
        <div className="flex items-center gap-3">
          <FaExclamationTriangle className="text-game-golden flex-shrink-0" aria-hidden="true" />
          <span className="font-medium">{title}</span>
        </div>
        <p className="mt-2 text-sm text-gray-200">{detail}</p>
      </div>
      {onRetry ? (
        <Button type="button" variant="primary" onClick={onRetry} className="w-full px-6 py-3">
          <FaRedo aria-hidden="true" />
          <span>Try again</span>
        </Button>
      ) : (
        <Button type="button" variant="primary" onClick={onDone} className="w-full px-6 py-3">
          {backLabel}
        </Button>
      )}
      {link && (
        <button
          type="button"
          onClick={link.onClick}
          className="w-full text-sm text-game-golden hover:text-game-accent underline-offset-2 hover:underline"
        >
          {link.label}
        </button>
      )}
    </div>
  );
};
